import { t } from '@/i18n';
import type { PublicEventQuestion } from '../types';

interface DynamicFieldProps {
  question: PublicEventQuestion;
  value: string | string[] | boolean | number;
  onChange: (questionId: number, value: string | string[] | boolean | number) => void;
  error?: string[];
}

const inputClasses = 'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-500';

export default function DynamicField({ question, value, onChange, error }: DynamicFieldProps) {
  const { id, label, field_type, required, choices_json, help_text } = question;

  function renderInput() {
    switch (field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(id, e.target.value)}
            className={inputClasses}
            placeholder={label}
          />
        );

      case 'textarea':
        return (
          <textarea
            rows={2}
            value={(value as string) || ''}
            onChange={(e) => onChange(id, e.target.value)}
            className={inputClasses}
            placeholder={label}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={(value as string) || ''}
            onChange={(e) => onChange(id, e.target.value)}
            className={inputClasses}
            placeholder={label}
          />
        );

      case 'select': {
        const choices = choices_json || [];
        return (
          <select
            value={(value as string) || ''}
            onChange={(e) => onChange(id, e.target.value)}
            className={inputClasses}
          >
            <option value="">{t('Select...')}</option>
            {choices.map((choice) => (
              <option key={choice} value={choice}>{choice}</option>
            ))}
          </select>
        );
      }

      case 'multiselect': {
        const choices = choices_json || [];
        const selected = Array.isArray(value) ? value : [];
        return (
          <div className="mt-1 space-y-2">
            {choices.map((choice) => (
              <label key={choice} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={selected.includes(choice)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, choice]
                      : selected.filter((c) => c !== choice);
                    onChange(id, next);
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
                />
                {choice}
              </label>
            ))}
          </div>
        );
      }

      case 'checkbox':
        return (
          <label className="mt-1 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(id, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
            />
            {label}
          </label>
        );

      default:
        return null;
    }
  }

  return (
    <div>
      {field_type !== 'checkbox' && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {field_type === 'checkbox' && required && (
        <span className="text-red-500 text-xs">{t('Required')}</span>
      )}
      {help_text && (
        <p className="text-xs text-gray-400 mt-0.5">{help_text}</p>
      )}
      {renderInput()}
      {error && error.map((err, i) => (
        <p key={i} className="mt-1 text-xs text-red-600 dark:text-red-400">{err}</p>
      ))}
    </div>
  );
}
